import React, { useLayoutEffect } from 'react';
import ScrollToTopButton from './common/ScrollToTopButton';

const TermsOfService = ({ onBack }) => {
  // Force scroll to top on mount - useLayoutEffect runs synchronously before paint
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div 
      data-public-scroll-container="true"
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        overflowX: 'hidden'
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Premium Legal Header */}
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 pb-4 mb-8 gap-4">
          <div className="flex items-center gap-3">
            <img src="/new_icon+text_color.png" alt="CraftMyParty" className="h-12 sm:h-14 w-auto" />
            <div className="h-6 w-px bg-gray-200 hidden sm:block"></div>
            <span className="hidden sm:inline-block text-xs font-semibold text-gray-400 uppercase tracking-wider">Your Party, Made Personal.</span>
          </div>
          <nav className="flex items-center gap-6 text-sm font-bold">
            <a href="/home" className="text-gray-600 hover:text-purple-600 transition">Home</a>
            <a href="/privacy" className="text-gray-600 hover:text-purple-600 transition">Privacy</a>
            <a href="/terms" className="text-purple-600 border-b-2 border-purple-600 pb-0.5 transition">Terms</a>
            <a href="/faq" className="text-gray-600 hover:text-purple-600 transition">FAQ</a>
          </nav>
        </header>

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-600">Last Updated: December 25th, 2025</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 md:p-10 prose prose-sm sm:prose max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              By accessing or using CraftMyParty (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
            </p>
            <p className="text-gray-700 leading-relaxed">
              These Terms apply to all users of the Service, including visitors and registered users.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of the Service</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              CraftMyParty is an AI-powered event planning platform designed to help users plan personal events. The Service may include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>AI-generated event themes, ideas, and suggestions</li>
              <li>Shopping lists and planning recommendations</li>
              <li>Location-based vendor or store suggestions</li>
              <li>Tools to save, organize, and manage event plans</li>
            </ul>
            <p className="text-gray-700 leading-relaxed font-semibold mb-3">
              The Service is intended for informational and planning purposes only.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The Service may incorporate third-party integrations, including authentication providers, content platforms, and external marketplaces. CraftMyParty does not control and is not responsible for the accuracy, availability, or practices of third-party services accessed through the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility and User Accounts</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Eligibility</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>You must be at least 13 years old to use the Service, or 16 if you are located in the EEA or UK</li>
              <li>By using the Service, you represent that you meet these requirements</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Account Registration</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>You agree to provide accurate and current information</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You are responsible for all activity that occurs under your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Account Termination</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>You may stop using the Service or delete your account at any time</li>
              <li>We may suspend or terminate your access if you violate these Terms or misuse the Service</li>
              <li>Upon termination, your access will end and data will be handled in accordance with our Privacy Policy</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Acceptable Use</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              You agree to use CraftMyParty only for lawful, personal, and non-commercial purposes.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Use the Service for illegal or unauthorized purposes</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property or privacy rights</li>
              <li>Attempt to reverse engineer, scrape, disrupt, or compromise the Service</li>
              <li>Use automated tools to access or extract data from the Service</li>
              <li>Submit harmful, misleading, or abusive content</li>
              <li>Interfere with the operation or security of the Service</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to investigate and take appropriate action for violations of this section.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. AI-Generated Content</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.1 Nature of AI Output</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Content generated by the Service is produced using artificial intelligence</li>
              <li>AI-generated content may be incomplete, inaccurate, or outdated</li>
              <li>Content is provided for planning inspiration only and should not be relied upon as professional, legal, financial, or safety advice</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.2 Your Responsibility</h3>
            <p className="text-gray-700 leading-relaxed mb-3">You are responsible for:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>Reviewing and validating AI-generated suggestions</li>
              <li>Confirming prices, availability, vendors, and logistics</li>
              <li>Ensuring compliance with local laws, venue rules, and safety requirements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              You acknowledge that decisions made based on AI-generated content are made at your own discretion.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">5.3 Ownership and Use of AI-Generated Content</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              Subject to these Terms, you may use AI-generated content provided by the Service for personal, non-commercial event planning purposes.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              CraftMyParty does not guarantee that AI-generated content is original, unique, or free from similarities to third-party materials. You are responsible for reviewing and determining whether AI-generated outputs are appropriate for your intended use.
            </p>
            <p className="text-gray-700 leading-relaxed">
              CraftMyParty makes no representations regarding intellectual property rights, suitability, or compliance of AI-generated content with third-party terms or policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Our Rights</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Service, including its design, features, branding, and underlying technology, is owned by CraftMyParty and protected by intellectual property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may not copy, modify, distribute, or create derivative works from the Service without our prior written consent.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2 Your Content</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              You retain ownership of the content and event plans you create or submit.
            </p>
            <p className="text-gray-700 leading-relaxed">
              By using the Service, you grant CraftMyParty a limited, non-exclusive, royalty-free license to use your content solely to operate, maintain, and improve the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Payments and Subscriptions</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Free and Paid Features</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some features may be available for free, while others may require payment or subscription.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">7.2 Paid Services</h3>
            <p className="text-gray-700 leading-relaxed mb-3">If you purchase paid features:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Fees are charged as described at the time of purchase</li>
              <li>Subscriptions may automatically renew unless cancelled</li>
              <li>Pricing and features may change with reasonable notice</li>
              <li>Refunds, if any, are handled according to the applicable purchase terms</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Services and Links</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              The Service may integrate with or link to third-party services, websites, or tools.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>We do not control third-party services</li>
              <li>Their terms and policies apply to your use of those services</li>
              <li>We are not responsible for third-party content, availability, or practices</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any interactions, purchases, or transactions you engage in with third-party services, including retailers, vendors, or marketplaces linked through the Service, are solely between you and the third party. CraftMyParty is not responsible for pricing, availability, fulfillment, refunds, or disputes arising from such interactions.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 Pinterest Integration</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you choose to connect your Pinterest account, you authorize CraftMyParty to access and use certain Pinterest data, such as boards or Pins, as permitted by your consent and Pinterest's APIs.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may revoke CraftMyParty's access to your Pinterest account at any time through your account settings or Pinterest's platform. CraftMyParty does not access your Pinterest credentials and is not responsible for Pinterest's services, content, or data practices, which are governed by Pinterest's own terms and privacy policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimers</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              The Service is provided on an "as is" and "as available" basis.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>We make no warranties regarding accuracy, reliability, or availability</li>
              <li>We do not guarantee uninterrupted or error-free operation</li>
              <li>We disclaim all implied warranties, including merchantability and fitness for a particular purpose</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed mb-3">To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
              <li>CraftMyParty will not be liable for indirect, incidental, consequential, or special damages</li>
              <li>Our total liability for any claim will not exceed the amount you paid us in the 12 months preceding the claim, or USD $100 if you paid nothing</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Some jurisdictions do not allow certain limitations, so some provisions may not apply to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              You agree to indemnify and hold harmless CraftMyParty and its affiliates from claims, losses, damages, and expenses arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Content you submit or actions you take using the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Dispute Resolution and Governing Law</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">12.1 Governing Law</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              These Terms are governed by the laws of the State of California, United States, without regard to conflict-of-law principles.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">12.2 Dispute Resolution</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We encourage informal resolution first. If a dispute cannot be resolved informally, it will be resolved in accordance with applicable law in the courts located in Ventura County, California, unless otherwise required by law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Nothing in this section limits consumer rights where mandatory laws apply.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to These Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may update these Terms from time to time. Material changes will be communicated through the Service or other reasonable means.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Termination</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may suspend or discontinue the Service, in whole or in part, with reasonable notice where practicable.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Sections that by their nature should survive termination, including intellectual property, disclaimers, and limitations of liability, will survive.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Entire Agreement</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and CraftMyParty regarding the Service and its use of AI-powered features and third-party integrations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">For questions about these Terms, contact:</p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-800 font-semibold mb-2">CraftMyParty</p>
              <p className="text-gray-700">Email: support@craftmyparty.com</p>
              <p className="text-gray-700">Address: Simi Valley, California, USA</p>
            </div>
          </section>
        </div>
      </div>
      <ScrollToTopButton targetSelector="[data-public-scroll-container='true']" />
    </div>
  );
};

export default TermsOfService;

